import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiLayers, FiDollarSign, FiPlus, FiBarChart2, FiFileText, FiTrash2 } from "react-icons/fi";
import { RiDownload2Line } from "react-icons/ri";
import { LuFactory } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import AgGrid from "../../../components/grid/AgGrid";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import ScopeEmissionGraph from "../../../net-zero-page-components/ScopeEmissionGraph";
// import "../style/pages-style/NetZero.css";

import * as htmlToImage from "html-to-image";
import PptxGenJS from "pptxgenjs";
import SaveButton from "../../../components/button/SaveButton";
import CostPerGWPChart from "../../../components/chart/CostPerGWPChart";
import GWPTotalChart from "../../../components/chart/GWPTotalChart";
import { MainContainer } from "../../../components/container/MainContainer";
import SquareCheckBox from "../../../components/input/SquareCheckBox";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef, tonsFormatter } from "../../../components/grid/columnDefinitions/columnDefinitions";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import { KpiGrid, KpiTile, DataTableCard, ChartCard, M3Surface, SegmentedButton, M3Button, Chip } from "../../../components/m3";
import { ORG_PALETTE } from "../../../theme/style/organizationPalette";
import { Structure } from "../../../db_access/structure";
import { Supply_chain } from "../../../db_access/supply_chain";
import DocumentViewer from "../../../organizational-page-components/DocumentViewer";
import NetZeroBottomLongTermTool from "../../../organizational-page-components/NetZeroBottomLongTermTool";
import NetZeroBottomShortTermTool from "../../../organizational-page-components/NetZeroBottomShortTermTool";
import { scope1Categories, scope2Categories, scope3Categories } from "../../Organization/ScopeCategory";

function NetZero() {
  const { t } = useTranslation();
  const { refresh, columnVisibilities } = useContextProvider();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const fixedScopes = [
    "Scope 1",
    "Scope 2 (location-based)",
    "Scope 2 (market-based)",
    "Scope 3, category 1: Purchased goods and services",
    "Scope 3, category 2: Capital goods",
    "Scope 3, category 3: Fuel- and energy-related activities",
    "Scope 3, category 4: Upstream transportation and distribution",
    "Scope 3, category 5: Waste generated in operations",
    "Scope 3, category 6: Business travel",
    "Scope 3, category 7: Employee commuting",
    "Scope 3, category 8: Upstream leased assets",
    "Scope 3, category 9: Downstream transportation and distribution",
    "Scope 3, category 10: Processing of sold products",
    "Scope 3, category 11: Use of sold products",
    "Scope 3, category 12: End-of-life treatment of sold products",
    "Scope 3, category 13: Downstream leased assets",
    "Scope 3, category 14: Franchises",
    "Scope 3, category 15: Investments",
    "Total: scope 1, scope 2 market-based, scope 3 (allcategories)",
  ];
  const scopes = [
    { label: "Scope 1", value: scope1Categories },
    { label: "Scope 2", value: scope2Categories },
    ...scope3Categories.map((c, i) => ({ label: "Scope 3, Category " + c, value: [c] })),
    { label: "Total", value: [...scope1Categories, ...scope2Categories, ...scope3Categories] },
  ];
  // your refs
  const gwpChartRef = useRef(null);
  const costPerGwpChartRef = useRef(null);
  const scopeEmissionGraphRef = useRef(null);
  const longShortToolRef = useRef(null);

  // const handleExportPowerpoint = async () => {
  //   try {
  //     const pptx = new PptxGenJS();
  //     pptx.layout = "LAYOUT_WIDE";

  //     const visibleOrganizations = selectedOrganizationIds.length > 0 ? organizations.filter((o) => selectedOrganizationIds.includes(o.id)) : organizations;

  //     const totalGwp = visibleOrganizations.reduce((sum, row) => sum + Number(row.GWP || row.gwp || 0), 0);
  //     const totalCost = visibleOrganizations.reduce((sum, row) => sum + Number(row.Cost || row.cost || 0), 0);

  //     const captureNode = async (node, options = {}) => {
  //       if (!node) return;

  //       const dataUrl = await htmlToImage.toPng(node, {
  //         backgroundColor: "#FFFFFF",
  //         pixelRatio: 2,
  //         filter: (n) => !(n.classList && n.classList.contains("export-ignore")),
  //       });

  //       const slide = pptx.addSlide();

  //       if (options.title) {
  //         slide.addText(options.title, {
  //           x: 0.5,
  //           y: 0.3,
  //           w: "90%",
  //           fontSize: 20,
  //           bold: true,
  //           color: "363636",
  //         });
  //       }

  //       if (options.subtitle) {
  //         slide.addText(options.subtitle, {
  //           x: 0.5,
  //           y: 0.8,
  //           w: "90%",
  //           fontSize: 12,
  //           color: "666666",
  //         });
  //       }

  //       slide.addImage({
  //         data: dataUrl,
  //         x: 0.5,
  //         y: 1.2,
  //         w: 20,
  //         h: 20,
  //       });
  //     };

  //     // 2) COVER SLIDE
  //     const cover = pptx.addSlide();
  //     cover.background = { fill: "F5F5F5" };
  //     cover.addText("Net Zero Dashboard", {
  //       x: 0.5,
  //       y: 2,
  //       w: 11,
  //       fontSize: 32,
  //       bold: true,
  //       align: "center",
  //       color: "1B5E20",
  //     });
  //     cover.addText(`View: ${activeTab}`, {
  //       x: 0.5,
  //       y: 3,
  //       w: 11,
  //       fontSize: 18,
  //       align: "center",
  //       color: "555555",
  //     });
  //     cover.addText(`Generated on ${new Date().toLocaleString()}`, {
  //       x: 0.5,
  //       y: 4,
  //       w: 11,
  //       fontSize: 12,
  //       align: "center",
  //       color: "777777",
  //     });

  //     // 3) SUMMARY SLIDE – pulls from your tables
  //     const summarySlide = pptx.addSlide();
  //     summarySlide.addText("Summary", {
  //       x: 0.5,
  //       y: 0.7,
  //       w: 11,
  //       fontSize: 26,
  //       bold: true,
  //       color: "1B5E20",
  //     });

  //     summarySlide.addText(
  //       `Total GWP emissions: ${totalGwp.toLocaleString(undefined, {
  //         maximumFractionDigits: 2,
  //       })} tCO₂e`,
  //       {
  //         x: 0.5,
  //         y: 1.4,
  //         w: 11,
  //         fontSize: 16,
  //         color: "333333",
  //       }
  //     );

  //     summarySlide.addText(
  //       `Total Cost: ${totalCost.toLocaleString(undefined, {
  //         maximumFractionDigits: 2,
  //       })}`,
  //       {
  //         x: 0.5,
  //         y: 1.9,
  //         w: 11,
  //         fontSize: 16,
  //         color: "333333",
  //       }
  //     );

  //     // (OPTIONAL) you can show selected orgs / year as bullet list here too

  //     // 4) THEN capture the graphs as images (see step 2 for making them “graph-only”)
  //     await captureNode(gwpChartRef.current, {
  //       title: "Total GWP Emissions",
  //     });

  //     await captureNode(costPerGwpChartRef.current, {
  //       title: "Cost per GWP",
  //     });

  //     await captureNode(scopeEmissionGraph2Ref.current, {
  //       title: "Scope Emission Graph (Yearly Based 2)",
  //     });

  //     await captureNode(scopeEmissionGraphRef.current, {
  //       title: "Scope Emission Graph",
  //     });

  //     if (documentView && selectedOrganizationIds.length === 1) {
  //       const org = organizations.find((o) => o.id === selectedOrganizationIds[0]) || {};
  //       await captureNode(documentViewerRef.current, {
  //         title: "Document View",
  //         subtitle: `Organization: ${org.name || ""}`,
  //       });
  //     }

  //     if (selectedOrganizationIds.length > 0) {
  //       const org = organizations.find((o) => selectedOrganizationIds.includes(o.id)) || {};
  //       await captureNode(longShortToolRef.current, {
  //         title: selectedTool === "long-term" ? "Long Term Tool" : "Short Term Tool",
  //         subtitle: `Organization: ${org.name || ""}`,
  //       });
  //     }

  //     await pptx.writeFile(`NetZero-${activeTab}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.pptx`);
  //   } catch (err) {
  //     console.error("Error exporting PowerPoint:", err);
  //     alert("Something went wrong while generating the PowerPoint.");
  //   }
  // };

  const initialColumnDefs = [
    {
      headerName: "Scope",
      field: "scope",
      ...defaultColDef,
      ...leftAlignColDef,
      headerCheckboxSelection: false,
      checkboxSelection: false,
    },
    {
      headerName: "Base year emissions (tCO₂e)",
      field: "column1",
      valueFormatter: (params) => formatFloatValue(params.value, 2),
      ...defaultColDef,
      ...rightAlignColDef,
    },
  ];
  // const initialRowData = fixedScopes.map((scope, index) => ({
  //   id: index,
  //   scope,
  //   column1: 0, // Initialize the first year to 0
  // }));
  const initialRowData = scopes.map((scope, index) => ({
    id: index,
    scope: scope.label,
    column1: 0, // Initialize the first year to 0
  }));
  const supply_chain = { label: "Supply Chain", value: "Supply Chain" };
  const organization = { label: "Organization", value: "Organization" };
  const tabs = [supply_chain, organization];
  const gridHeaderContainerRef = useRef();
  const [activeTab, setActiveTab] = useState(supply_chain.value);
  const [organizationColumnDefs, setOrganizationColumnDefs] = useState(initialColumnDefs);
  const organizationColumnDefsRef = useRef(organizationColumnDefs);
  const [valueChainColMap, setValueChainColMap] = useState({});
  const [valueChainColumnDefs, setValueChainColumnDefs] = useState(initialColumnDefs);
  const valueChainColumnDefsRef = useRef(valueChainColumnDefs);
  const [corporateColumnDefs, setCorporateColumnDefs] = useState(initialColumnDefs);
  const corporateColumnDefsRef = useRef(corporateColumnDefs);
  const [valueChainRowData, setValueChainRowData] = useState(initialRowData);
  const valueChainRowDataRef = useRef(valueChainRowData);
  const [organizationRowData, setOrganizationRowData] = useState(initialRowData);
  const organizationRowDataRef = useRef(organizationRowData);
  const [corporateRowData, setCorporateRowData] = useState(initialRowData);
  const corporateRowDataRef = useRef(corporateRowData);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const selectedRowDataRef = useRef(selectedRowData);
  const colVisibility_key = "net_zero_org_col_visibility";
  const defaultCols = ["name", "year", "level", "gwp", "functional_unit", "role"];
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const columnMenuAnchorRef = useRef(null);
  const [mainTableGridApi, setMainTableGridApi] = useState(null);
  const [trackViewState, setTrackViewState] = useState(false);
  const [allTableRows, setAllTableRows] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [pageType, setPageType] = useState("valueChain");
  const pageTypeRef = useRef(pageType);
  const netZeroPage = true;
  const [selectedScope, setSelectedScope] = useState([]);
  const [selectedTool, setSelectedTool] = useState("short-term");
  const gridRef = useRef();
  const [organizations, setOrganizations] = useState([]);
  const organizationsRef = useRef(organizations);
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState([]);
  const selectedOrganizationIdsRef = useRef(selectedOrganizationIds);
  const lastClickedOrganizationRef = useRef(null);
  const [GHGs, setGHGs] = useState([]);
  const GHGsRef = useRef(GHGs);

  const [globalView, setGlobalView] = useState(false);
  const [documentView, setDocumentView] = useState(false);
  const [structureView, setStructureView] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [hideZeroCategories, setHideZeroCategories] = useState(true);
  const [pendingTargetColumnField, setPendingTargetColumnField] = useState(null);
  const pendingTargetColumnFieldRef = useRef(null);
  const skipBaseYearPopulateRef = useRef(false);

  const activeYearlyRowData = useMemo(() => {
    const raw =
      activeTab === "Supply Chain"
        ? valueChainRowData
        : activeTab === "Organization"
        ? organizationRowData
        : corporateRowData;
    if (!hideZeroCategories) return raw;

    const filtered = raw.filter((row) => {
      const colFields = Object.keys(row).filter((k) => k.startsWith("column"));
      const hasEmissions = colFields.some((f) => Math.abs(Number(row[f]) || 0) > 0.0001);
      const isTotal = typeof row.scope === "string" && row.scope.toLowerCase().includes("total");
      return hasEmissions || isTotal;
    });

    return filtered.length > 0 ? filtered : raw;
  }, [activeTab, valueChainRowData, organizationRowData, corporateRowData, hideZeroCategories]);
  useEffect(() => {
    selectedRowDataRef.current = selectedRowData;
  }, [selectedRowData]);
  useEffect(() => {
    valueChainColumnDefsRef.current = valueChainColumnDefs;
  }, [valueChainColumnDefs]);
  useEffect(() => {
    organizationColumnDefsRef.current = organizationColumnDefs;
  }, [organizationColumnDefs]);
  useEffect(() => {
    corporateColumnDefsRef.current = corporateColumnDefs;
  }, [corporateColumnDefs]);
  useEffect(() => {
    valueChainRowDataRef.current = valueChainRowData;
  }, [valueChainRowData]);
  useEffect(() => {
    organizationRowDataRef.current = organizationRowData;
  }, [organizationRowData]);
  useEffect(() => {
    corporateRowDataRef.current = corporateRowData;
  }, [corporateRowData]);
  useEffect(() => {
    pageTypeRef.current = activeTab;
  }, [activeTab]);
  useEffect(() => {
    const enrich = (data, getScopeGwp) => {
      const ids = data.map((d) => d.id);
      getScopeGwp(ids)
        .then((res) => res.json())
        .then((scopeData) => {
          if (!Array.isArray(scopeData)) return setOrganizations(data);
          const fossilMap = {};
          scopeData.forEach((d) => {
            fossilMap[d.id] = (Number(d.scope_1) || 0) + (Number(d.scope_2) || 0) + (Number(d.scope_3) || 0);
          });
          setOrganizations(data.map((sc) => ({ ...sc, gwp: fossilMap[sc.id] ?? sc.gwp })));
        })
        .catch(() => setOrganizations(data));
    };

    if (activeTab === supply_chain.value) {
      Supply_chain.get_mine()
        .then((response) => response.json())
        .then((data) => enrich(data, Supply_chain.get_total_gwp_by_scope));
    } else if (activeTab === organization.value) {
      Structure.get_mine()
        .then((response) => response.json())
        .then((data) => enrich(data, Structure.get_total_gwp_by_scope));
    } else {
      setOrganizations([]);
    }
  }, [activeTab, refresh]);
  const populateEmissionsToColumn = (ghgList, field = "column1", orgYear = null) => {
    const list = Array.isArray(ghgList) ? ghgList : [];
    const result = {};
    list.forEach((item) => {
      const category = item.category;
      const gwp = Number(item.gwp) || 0;
      if (!result[category]) {
        result[category] = 0;
      }
      result[category] += gwp;
    });

    const temp = Object.entries(result).map(([scope, value]) => ({ scope, value }));
    const computedData = scopes.map((scope) => {
      const matches = temp.filter((item) => scope.value.includes(item.scope));
      const totalValue = matches.reduce((sum, item) => sum + item.value, 0);
      return { scope: scope.label, value: Number((totalValue / 1000).toFixed(2)) };
    });

    if (activeTab === supply_chain.value) {
      setValueChainRowData((prev) =>
        prev.map((row, index) => ({
          ...row,
          [field]: index < computedData.length ? computedData[index].value : 0,
        }))
      );
      if (orgYear) {
        setValueChainColMap((prev) => ({ ...prev, [field]: String(orgYear) }));
        setValueChainColumnDefs((prev) =>
          prev.map((colDef, idx) => {
            if (colDef.field === field) {
              return {
                ...colDef,
                headerName: idx === 1 ? `${t("Base year emissions (tCO₂e)")} - ${orgYear}` : `${t("Yearly emissions (tCO₂e)")} - ${orgYear}`,
              };
            }
            return colDef;
          })
        );
      }
    } else {
      setOrganizationRowData((prev) =>
        prev.map((row, index) => ({
          ...row,
          [field]: index < computedData.length ? computedData[index].value : 0,
        }))
      );
      if (orgYear) {
        setValueChainColMap((prev) => ({ ...prev, [field]: String(orgYear) }));
        setOrganizationColumnDefs((prev) =>
          prev.map((colDef, idx) => {
            if (colDef.field === field) {
              return {
                ...colDef,
                headerName: idx === 1 ? `${t("Base year emissions (tCO₂e)")} - ${orgYear}` : `${t("Yearly emissions (tCO₂e)")} - ${orgYear}`,
              };
            }
            return colDef;
          })
        );
      }
    }
  };

  const populatePendingColumnWithOrg = (clickedOrg) => {
    const targetField = pendingTargetColumnFieldRef.current;
    if (!targetField || !clickedOrg?.id) return false;

    // Clear pending target immediately to avoid double-firing or race conditions
    pendingTargetColumnFieldRef.current = null;
    setPendingTargetColumnField(null);
    skipBaseYearPopulateRef.current = true;
    lastClickedOrganizationRef.current = clickedOrg;

    const fetchEmissions =
      activeTab === supply_chain.value
        ? Supply_chain.getEmissions(clickedOrg.id)
        : Structure.getEmissions(clickedOrg.id);

    fetchEmissions
      .then((response) => response.json())
      .then((data) => {
        populateEmissionsToColumn(data, targetField, clickedOrg.year);
      })
      .catch((err) => {
        console.error("Error populating emissions for", targetField, err);
      });

    return true;
  };

  useEffect(() => {
    if (skipBaseYearPopulateRef.current) {
      skipBaseYearPopulateRef.current = false;
      return;
    }
    if (selectedOrganizationIds.length === 0) {
      setGHGs([]);
      setValueChainColumnDefs(initialColumnDefs);
      setValueChainRowData(initialRowData);
      setOrganizationColumnDefs(initialColumnDefs);
      setOrganizationRowData(initialRowData);
      setValueChainColMap({});
      return;
    }
    const currentOrg = organizations.find((o) => o.id === selectedOrganizationIds[0]);
    if (activeTab === supply_chain.value) {
      Supply_chain.getEmissions(selectedOrganizationIds[0])
        .then((response) => response.json())
        .then((data) => {
          setGHGs(data);
          populateEmissionsToColumn(data, "column1", currentOrg?.year);
        });
    } else if (activeTab === organization.value) {
      Structure.getEmissions(selectedOrganizationIds[0])
        .then((response) => response.json())
        .then((data) => {
          setGHGs(data);
          populateEmissionsToColumn(data, "column1", currentOrg?.year);
        });
    } else {
      setGHGs([]);
    }
  }, [selectedOrganizationIds, activeTab, organizations]);
  useEffect(() => {
    GHGsRef.current = GHGs;
  }, [GHGs]);
  useEffect(() => {
    organizationsRef.current = organizations;
  }, [organizations]);
  useEffect(() => {
    selectedOrganizationIdsRef.current = selectedOrganizationIds;
  }, [selectedOrganizationIds]);
  useEffect(() => {
    if (activeTab === supply_chain.value) {
      setSelectedScope(valueChainRowData);
    } else if (activeTab === organization.value) {
      setSelectedScope(organizationRowData);
    }
  }, [valueChainRowData, activeTab, organizationRowData]);
  const onGridReady = (params) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };
  const handleAddButtonClick = async (field) => {
    try {
      const currentPageType = pageTypeRef.current;
      const currentSelectedRowData = lastClickedOrganizationRef.current;

      if (!currentSelectedRowData || !currentSelectedRowData.id) {
        return;
      }

      const emissionResponse = currentPageType === supply_chain.value
        ? await Supply_chain.getEmissions(currentSelectedRowData.id)
        : await Structure.getEmissions(currentSelectedRowData.id);
      const ghg = await emissionResponse.json();

      if (!ghg || ghg.length === 0) {
        return;
      }

      const result = {};

      ghg.forEach((item) => {
        const category = item.category;
        const gwp = Number(item.gwp) || 0;
        if (!result[category]) {
          result[category] = 0;
        }
        result[category] += gwp;
      });

      const temp = Object.entries(result).map(([scope, value]) => ({ scope, value }));

      const data = scopes.map((scope) => {
        const matches = temp.filter((item) => {
          return scope.value.includes(item.scope);
        });
        const totalValue = matches.reduce((sum, item) => sum + item.value, 0);
        return { scope: scope.label, value: Number((totalValue / 1000).toFixed(2)) };
      });
      // Check if the fetched data contains the correct structure
      if (!Array.isArray(data)) {
        console.error("Invalid data format from API");
        return;
      }

      // Update rowData with the fetched values
      switch (currentPageType) {
        case supply_chain.value:
          setValueChainRowData(
            valueChainRowDataRef.current.map((row, index) => ({
              ...row,
              [field]: index < data.length ? data[index].value : 0,
            }))
          );
          break;
        case organization.value:
          setOrganizationRowData(
            organizationRowDataRef.current.map((row, index) => ({
              ...row,
              [field]: index < data.length ? data[index].value : 0,
            }))
          );
          break;
        default:
          console.warn("Unknown page type");
          break;
      }

      if (currentPageType === "Supply Chain") {
        const updatedColumnDefs = valueChainColumnDefsRef.current.map((colDef, index) => {
          if (colDef.field === field) {
            if (index === 1) {
              return { ...colDef, headerName: `${t("Base year emissions (tCO₂e)")} - ${currentSelectedRowData.year}` };
            } else {
              return { ...colDef, headerName: `${t("Yearly emissions (tCO₂e)")} - ${currentSelectedRowData.year}` };
            }
          }
          return colDef;
        });
        setValueChainColumnDefs(updatedColumnDefs);
        setValueChainColMap((prevYears) => ({ ...prevYears, [field]: currentSelectedRowData.year }));
      } else if (currentPageType === "Organization") {
        const updatedColumnDefs = organizationColumnDefsRef.current.map((colDef, index) => {
          if (colDef.field === field) {
            if (index === 1) {
              return { ...colDef, headerName: `${t("Base year emissions (tCO₂e)")} - ${currentSelectedRowData.year}` };
            } else {
              return { ...colDef, headerName: `${t("Yearly emissions (tCO₂e)")} - ${currentSelectedRowData.year}` };
            }
          }
          return colDef;
        });
        setOrganizationColumnDefs(updatedColumnDefs);
      } else if (pageTypeRef.current === "corporate") {
        const updatedColumnDefs = corporateColumnDefsRef.current.map((colDef, index) => {
          if (colDef.field === field) {
            if (index === 1) {
              return { ...colDef, headerName: `${t("Base year emissions (tCO₂e)")} - ${currentSelectedRowData.year}` };
            } else {
              return { ...colDef, headerName: `${t("Yearly emissions (tCO₂e)")} - ${currentSelectedRowData.year}` };
            }
          }
          return colDef;
        });
        setCorporateColumnDefs(updatedColumnDefs);
      } else {
        console.warn("Unknown page type");
      }
    } catch (error) {
      console.error("Error fetching data from backend:", error);
    }
  };

  const handleDeleteColumnClick = (field) => {
    if (field === pendingTargetColumnFieldRef.current) {
      pendingTargetColumnFieldRef.current = null;
      setPendingTargetColumnField(null);
    }
    const currentPageType = pageTypeRef.current;
    if (currentPageType === "Supply Chain") {
      const updatedColumnDefs = valueChainColumnDefsRef.current.filter((colDef) => colDef.field !== field);
      setValueChainColumnDefs(updatedColumnDefs);
      setValueChainColMap((prevYears) => {
        const { [field]: _, ...rest } = prevYears;
        return rest;
      });
    } else if (currentPageType === "Organization") {
      const updatedColumnDefs = organizationColumnDefsRef.current.filter((colDef) => colDef.field !== field);
      setOrganizationColumnDefs(updatedColumnDefs);
      setValueChainColMap((prevYears) => {
        const { [field]: _, ...rest } = prevYears;
        return rest;
      });
    } else if (currentPageType === "corporate") {
      const updatedColumnDefs = corporateColumnDefsRef.current.filter((colDef) => colDef.field !== field);
      setCorporateColumnDefs(updatedColumnDefs);
    } else {
      console.warn("Unknown page type");
    }

    if (currentPageType === "Supply Chain") {
      const updatedRowData = valueChainRowDataRef.current.map((row) => {
        const { [field]: _, ...rest } = row;
        return rest;
      });
      setValueChainRowData(updatedRowData);
    } else if (currentPageType === "Organization") {
      const updatedRowData = organizationRowDataRef.current.map((row) => {
        const { [field]: _, ...rest } = row;
        return rest;
      });
      setOrganizationRowData(updatedRowData);
    } else if (currentPageType === "corporate") {
      const updatedRowData = corporateRowDataRef.current.map((row) => {
        const { [field]: _, ...rest } = row;
        return rest;
      });
      setCorporateRowData(updatedRowData);
    } else {
      console.warn("Unknown page type");
    }
  };

  function formatFloatValue(value, decimalPlaces = 2) {
    const num = Number(value);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-CA", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }
  const colDefs = useMemo(() => {
    return [
      { headerName: "Name", field: "name", ...defaultColDef, ...leftAlignColDef, headerCheckboxSelection: true, checkboxSelection: false },
      { headerName: "Year", field: "year", ...defaultColDef, ...centerAlignColDef },
      { headerName: "Level", field: "level", hide: activeTab === "Organization", ...defaultColDef, ...leftAlignColDef },
      { headerName: "GWP (tCO₂e)", field: "gwp", ...defaultColDef, ...rightAlignColDef, ...tonsFormatter },
      { headerName: "Cost (USD)", field: "cost", hide: true, ...defaultColDef, ...rightAlignColDef, ...floatFormater },
      {
        headerName: "Cost per GWP",
        field: "cost_per_gwp",
        hide: true,
        valueGetter: (params) => {
          const gwpKg = Number(params.data?.gwp) || 0;
          const cost = Number(params.data?.cost) || 0;
          if (gwpKg === 0) return 0;
          return cost / (gwpKg / 1000);
        },
        ...defaultColDef,
        ...rightAlignColDef,
        ...floatFormater,
      },
      { headerName: "Functional unit", field: "functional_unit", hide: activeTab === "Organization", ...defaultColDef, ...leftAlignColDef },
      { headerName: "Role", field: "role", hide: activeTab === "Organization", ...defaultColDef, ...leftAlignColDef },
    ].map((col) => {
      const isVisibleInSettings = columnVisibilities[colVisibility_key]
        ? columnVisibilities[colVisibility_key].includes(col.field)
        : defaultCols.includes(col.field);

      let isHidden = !isVisibleInSettings;
      if (activeTab === "Organization" && (col.field === "level" || col.field === "functional_unit" || col.field === "role")) {
        isHidden = true;
      }
      return {
        ...col,
        hide: isHidden,
      };
    });
  }, [columnVisibilities, colVisibility_key, activeTab, defaultCols]);
  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    if (pendingTargetColumnFieldRef.current && selected.length > 0) {
      const clickedOrg = selected[selected.length - 1];
      populatePendingColumnWithOrg(clickedOrg);
      return;
    }
    setSelectedOrganizationIds(selected.map((s) => s.id));
  };

  const onOrganizationRowClicked = (event) => {
    const clickedOrg = event.data;
    lastClickedOrganizationRef.current = clickedOrg;
    if (pendingTargetColumnFieldRef.current && clickedOrg?.id) {
      populatePendingColumnWithOrg(clickedOrg);
    }
  };
  const onSelectedScopes = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedScopes(selected.map((s) => s.scope));
    setSelectedScope(selected);
  };

  const onYearlyCellValueChanged = (params) => {
    const { data, colDef, newValue } = params;
    const field = colDef.field;
    const val = Number(newValue) || 0;
    if (activeTab === supply_chain.value) {
      setValueChainRowData((prev) =>
        prev.map((row) => (row.id === data.id ? { ...row, [field]: val } : row))
      );
    } else {
      setOrganizationRowData((prev) =>
        prev.map((row) => (row.id === data.id ? { ...row, [field]: val } : row))
      );
    }
  };

  const addColumn = () => {
    const currentOrg = organizations.find((o) => o.id === selectedOrganizationIds[0]);
    const baseYearNum = Number(currentOrg?.year) || new Date().getFullYear();

    if (activeTab === supply_chain.value) {
      const colIndex = valueChainColumnDefs.length;
      const nextYear = baseYearNum + (colIndex - 1);
      const newFieldName = `column${colIndex}`;
      const newColumn = {
        headerName: `${t("Yearly emissions (tCO₂e)")} - ${nextYear}`,
        field: newFieldName,
        ...defaultColDef,
        ...rightAlignColDef,
        editable: true,
        cellEditor: "agTextCellEditor",
        valueFormatter: (params) => formatFloatValue(params.value, 2),
      };

      setValueChainColMap((prev) => ({ ...prev, [newFieldName]: String(nextYear) }));
      const updatedColumnDefs = [...valueChainColumnDefsRef.current, newColumn];
      setValueChainColumnDefs((p) => [...p, newColumn]);
      setPendingTargetColumnField(newFieldName);
      pendingTargetColumnFieldRef.current = newFieldName;

      const updatedRowData = valueChainRowData.map((row) => ({
        ...row,
        [newFieldName]: 0,
      }));
      setValueChainRowData(updatedRowData);
    } else if (activeTab === "Organization") {
      const colIndex = organizationColumnDefs.length;
      const nextYear = baseYearNum + (colIndex - 1);
      const newFieldName = `column${colIndex}`;
      const newColumn = {
        headerName: `${t("Yearly emissions (tCO₂e)")} - ${nextYear}`,
        field: newFieldName,
        ...defaultColDef,
        ...rightAlignColDef,
        editable: true,
        cellEditor: "agTextCellEditor",
        valueFormatter: (params) => formatFloatValue(params.value, 2),
      };

      setValueChainColMap((prev) => ({ ...prev, [newFieldName]: String(nextYear) }));
      const updatedColumnDefs = [...organizationColumnDefsRef.current, newColumn];
      setOrganizationColumnDefs((p) => [...p, newColumn]);
      setPendingTargetColumnField(newFieldName);
      pendingTargetColumnFieldRef.current = newFieldName;

      const updatedRowData = organizationRowData.map((row) => ({
        ...row,
        [newFieldName]: 0,
      }));
      setOrganizationRowData(updatedRowData);
    } else {
      console.warn("Unknown page type");
    }
  };

  const removeLastColumn = () => {
    if (activeTab === supply_chain.value) {
      if (valueChainColumnDefs.length > 2) {
        const lastCol = valueChainColumnDefs[valueChainColumnDefs.length - 1];
        handleDeleteColumnClick(lastCol.field);
      }
    } else if (activeTab === "Organization") {
      if (organizationColumnDefs.length > 2) {
        const lastCol = organizationColumnDefs[organizationColumnDefs.length - 1];
        handleDeleteColumnClick(lastCol.field);
      }
    }
  };

  const onColumnMovedHandler = () => {
    if (gridApi && gridColumnApi) {
      // Get all columns in their new order
      const allColumns = gridColumnApi.getAllDisplayedColumns();

      // Map the updated column definitions in the new order
      const newColumnDefs = allColumns.map((column) => column.getColDef());

      // Update the column definitions for the appropriate pageType
      switch (activeTab) {
        case supply_chain.value:
          setValueChainColumnDefs(newColumnDefs);
          break;
        case organization.value:
          setOrganizationColumnDefs(newColumnDefs);
          break;
        default:
          break;
      }
      // Get the column order by their field IDs
      const columnOrder = allColumns.map((col) => col.getColId());

      // Reorder the selected rows based on the new column order
      const selectedRows = gridApi.getSelectedRows();
      const reorderedSelectedRows = selectedRows.map((row) => {
        const reorderedRow = {};
        columnOrder.forEach((field) => {
          // Retain only the fields in the new column order
          if (row.hasOwnProperty(field)) {
            reorderedRow[field] = row[field];
          }
        });
        return reorderedRow;
      });

      // Log reordered rows for debugging

      // Update the state for selected rows in the new order
      setSelectedScope(reorderedSelectedRows);
    }
  };
  const totalGWP = useMemo(() => organizations.reduce((sum, org) => sum + (Number(org.gwp) || 0), 0), [organizations]);
  const totalCost = useMemo(() => organizations.reduce((sum, org) => sum + (Number(org.cost) || 0), 0), [organizations]);

  const onColumnMoved = (event) => {
    // Get all columns in their new order

    const newColumnDefs = event.api.getColumnDefs();

    // Update the column definitions for the appropriate pageType
    switch (activeTab) {
      case supply_chain.value:
        setValueChainColumnDefs(newColumnDefs);
        break;
      case organization.value:
        setOrganizationColumnDefs(newColumnDefs);
        break;
      default:
        break;
    }

    // // Get the column order by their field IDs
    // const columnOrder = allColumns.map((col) => col.getColId());

    // // Reorder the selected rows based on the new column order
    // const selectedRows = gridApi.getSelectedRows();
    // const reorderedSelectedRows = selectedRows.map((row) => {
    //   const reorderedRow = {};
    //   columnOrder.forEach((field) => {
    //     // Retain only the fields in the new column order
    //     if (row.hasOwnProperty(field)) {
    //       reorderedRow[field] = row[field];
    //     }
    //   });
    //   return reorderedRow;
    // });

    // // Log reordered rows for debugging
    // console.log("Reordered Selected Rows:", reorderedSelectedRows);

    // // Update the state for selected rows in the new order
    // setSelectedScope(reorderedSelectedRows);
  };

  return (
    <MainContainer>
      <Box sx={{ mb: 2.5 }}>
        <KpiGrid columns={2}>
          <KpiTile
            label={t("Total GWP")}
            value={Math.round(totalGWP / 1000).toLocaleString()}
            unit="tCO₂e"
            icon={<LuFactory size={24} />}
            tone="gwp"
            size="lg"
          />
          <KpiTile
            label={t("Total Cost")}
            value={Math.round(totalCost).toLocaleString()}
            unit="USD"
            icon={<FiDollarSign size={24} />}
            tone="cost"
            size="lg"
          />
        </KpiGrid>
      </Box>

      <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-start" }}>
        <SegmentedButton
          options={[
            { value: "Supply Chain", label: t("Supply Chain") },
            { value: "Organization", label: t("Organization") },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel={t("Filter sector")}
        />
      </Box>

      <DataTableCard
        title={`${t(activeTab)} (${organizations.length})`}
        actions={
          <Box ref={columnMenuAnchorRef}>
            <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
          </Box>
        }
      >
        <AgGrid
          ref={gridRef}
          columnDefs={colDefs}
          rowData={organizations}
          onSelectionChanged={onSelectionChanged}
          onRowClicked={onOrganizationRowClicked}
          headerCheckbox={false}
          checkboxes={false}
          domLayout="autoHeight"
          footerLeftContent={
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <SquareCheckBox
                checked={globalView}
                onChange={(e) => setGlobalView(e.target.checked)}
                label={t("Global View")}
              />
              {selectedOrganizationIds.length === 1 && (
                <SquareCheckBox
                  checked={documentView}
                  onChange={(e) => setDocumentView(e.target.checked)}
                  label={t("Document View")}
                />
              )}
            </Box>
          }
        />
      </DataTableCard>

      {globalView && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mt: 4 }}>
          <ChartCard title={t("Total GWP Emissions (kg CO₂e)")}>
            <div ref={gwpChartRef}>
              <GWPTotalChart selectedRows={organizations} showHeader={false} />
            </div>
          </ChartCard>
          <ChartCard title={t("Cost per GWP ($/kg CO₂e)")}>
            <div ref={costPerGwpChartRef}>
              <CostPerGWPChart selectedRows={organizations} showHeader={false} />
            </div>
          </ChartCard>
        </Box>
      )}

      {documentView && selectedOrganizationIds.length === 1 && (
        <Box sx={{ mt: 4 }}>
          <DocumentViewer pageType={pageType} selectedRow={organizations.find((o) => o.id === selectedOrganizationIds[0])} />
        </Box>
      )}

      {/* YEARLY BASED GHG – DataTableCard without outer side padding on the grid */}
      <Box sx={{ mt: 4 }}>
        <DataTableCard
          title={t("Yearly Based GHG")}
          actions={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <SquareCheckBox
                checked={hideZeroCategories}
                onChange={(e) => setHideZeroCategories(e.target.checked)}
                label={t("Hide zero categories")}
              />
              {pendingTargetColumnField && (
                <Chip
                  tone="info"
                  size="sm"
                  label={t("Select a row in the table above to populate emissions")}
                  onDelete={() => {
                    pendingTargetColumnFieldRef.current = null;
                    setPendingTargetColumnField(null);
                  }}
                />
              )}
              <M3Button variant="tonal" size="sm" onClick={addColumn} icon={<FiPlus size={16} />}>
                {t("Add year")}
              </M3Button>
              {((activeTab === "Supply Chain" && valueChainColumnDefs.length > 2) ||
                (activeTab === "Organization" && organizationColumnDefs.length > 2)) && (
                <M3Button variant="outlined" size="sm" onClick={removeLastColumn} icon={<FiTrash2 size={16} />}>
                  {t("Remove year")}
                </M3Button>
              )}
            </Box>
          }
          className="export-ignore"
        >
          <AgGrid
            columnDefs={activeTab === "Supply Chain" ? valueChainColumnDefs : activeTab === "Organization" ? organizationColumnDefs : corporateColumnDefs}
            rowData={activeYearlyRowData}
            onColumnMoved={onColumnMoved}
            onCellValueChanged={onYearlyCellValueChanged}
            headerCheckbox={false}
            checkboxes={false}
            singleClickEdit={true}
            domLayout="autoHeight"
          />
        </DataTableCard>
      </Box>

      {/* SCOPE EMISSION GRAPH */}
      {selectedOrganizationIds.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <div ref={scopeEmissionGraphRef}>
            <ScopeEmissionGraph
              scopeEmissionData={activeYearlyRowData}
              columnDefs={activeTab === "Supply Chain" ? valueChainColumnDefs : activeTab === "Organization" ? organizationColumnDefs : corporateColumnDefs}
              yearMapByColumnField={valueChainColMap}
            />
          </div>
        </Box>
      )}

      {/* LONG / SHORT TERM TOOL */}
      {selectedOrganizationIds.length > 0 && (
        <M3Surface style={{ marginTop: "24px" }} ref={longShortToolRef}>
          <Box className="export-ignore" sx={{ p: 2, pb: 0 }}>
            <SegmentedButton
              options={[
                { label: t("Long Term Tool"), value: "long-term" },
                { label: t("Short Term Tool"), value: "short-term" },
              ]}
              value={selectedTool}
              onChange={setSelectedTool}
            />
          </Box>
          <Box sx={{ display: selectedTool === "long-term" ? "block" : "none", p: 2 }}>
            <NetZeroBottomLongTermTool
              selectedRow={organizations.find((o) => selectedOrganizationIds.includes(o.id))}
              page={activeTab}
              yearlyRowData={activeYearlyRowData}
              yearlyColumnDefs={activeTab === "Supply Chain" ? valueChainColumnDefs : activeTab === "Organization" ? organizationColumnDefs : corporateColumnDefs}
              yearMapByColumnField={valueChainColMap}
              organizations={organizations}
              selectedOrganizationIds={selectedOrganizationIds}
            />
          </Box>
          <Box sx={{ display: selectedTool === "short-term" ? "block" : "none", p: 2 }}>
            <NetZeroBottomShortTermTool
              selectedRow={organizations.find((o) => selectedOrganizationIds.includes(o.id))}
              page={activeTab}
              yearlyRowData={activeYearlyRowData}
              yearlyColumnDefs={activeTab === "Supply Chain" ? valueChainColumnDefs : activeTab === "Organization" ? organizationColumnDefs : corporateColumnDefs}
              yearMapByColumnField={valueChainColMap}
              organizations={organizations}
              selectedOrganizationIds={selectedOrganizationIds}
            />
          </Box>
        </M3Surface>
      )}

      <ColumnMenu
        anchorRef={columnMenuAnchorRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />

      {/* SAVE BUTTON */}
      <div className="export-ignore">
        <SaveButton />
      </div>
    </MainContainer>
  );
}

export default NetZero;
